"use strict";
/**
 * PNL page rendering — traded-events list and the per-event detail view.
 *
 * Pure DOM rendering (no data fetching); the handler owns loading and wiring.
 * Mobile-first: rows are tap targets, no hover-only affordances (the operator
 * is primarily on a touchscreen).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.usd = exports.parseInterval = exports.intervalLabel = exports.DEFAULT_DRIFT_INTERVAL_S = void 0;
exports.driftTier = driftTier;
exports.summarizeEv = summarizeEv;
exports.resolveDriftInterval = resolveDriftInterval;
exports.pnlCell = pnlCell;
exports.esc = esc;
exports.renderPnlEventsList = renderPnlEventsList;
exports.applyDriftInterval = applyDriftInterval;
exports.renderPnlDetail = renderPnlDetail;
/**
 * Drift tint cut-offs, in DOLLARS of post-trade midpoint movement. Below the
 * first cut-off a trade is left untinted: sub-half-cent drift is book noise, and
 * tinting it would make every row coloured and the scale meaningless.
 */
const DRIFT_TIERS = [0.005, 0.02, 0.05];
/** The interval to select when nothing is stored and the server offers it. */
exports.DEFAULT_DRIFT_INTERVAL_S = 13;
/**
 * How an interval reads in the UI. The operator-invoked sample is labelled
 * MARK — the same word as the button that records it, so the picker and the
 * orderbook control obviously refer to the same thing.
 */
const intervalLabel = (interval) => interval === "user" ? "MARK" : `${interval}s`;
exports.intervalLabel = intervalLabel;
/** Parse a chip's data-interval back into an interval. */
const parseInterval = (raw) => {
    if (raw === "user")
        return "user";
    const seconds = Number(raw);
    return Number.isFinite(seconds) ? seconds : null;
};
exports.parseInterval = parseInterval;
/**
 * Row-tint class for a drift figure: three strengths per direction, or "" for
 * noise/unknown. Pure — the tier boundaries are the whole colour scale, so this
 * is the one place they're defined.
 */
function driftTier(dollars) {
    if (dollars === null ||
        dollars === undefined ||
        !Number.isFinite(dollars) ||
        Math.abs(dollars) < DRIFT_TIERS[0]) {
        return "";
    }
    const magnitude = Math.abs(dollars);
    const level = magnitude >= DRIFT_TIERS[2] ? 3 : magnitude >= DRIFT_TIERS[1] ? 2 : 1;
    return `pnl-ev-${dollars > 0 ? "pos" : "neg"}-${level}`;
}
/** Drift in signed cents, one decimal; an em dash when it isn't known. */
function fmtDrift(dollars) {
    if (dollars === null || dollars === undefined || !Number.isFinite(dollars))
        return "—";
    const cents = dollars * 100;
    // Round before signing so -0.04¢ reads "0.0¢", not "-0.0¢". The sign glyph
    // matches pnlCell's, so the per-share and total columns agree in a row.
    const rounded = Math.round(cents * 10) / 10;
    const sign = rounded > 0 ? "+" : rounded < 0 ? "-" : "";
    return `${sign}${Math.abs(rounded).toFixed(1)}¢`;
}
/** A trade's drift at one interval; undefined when the leg was never tracked. */
const driftAt = (trade, interval) => trade.drift?.[interval];
/**
 * The trade's TOTAL edge in dollars: per-share drift × contracts filled.
 *
 * The per-share baseline (`avg_fill_price_with_fees`) already carries the
 * per-share fee, so this product is net of the leg's whole fee — no separate
 * fee term. Null/undefined propagate: an unknown edge has no total.
 */
const totalDriftAt = (trade, interval) => {
    const perShare = driftAt(trade, interval);
    return perShare === null || perShare === undefined
        ? perShare
        : perShare * trade.quantity;
};
function summarizeEv(trades, interval) {
    let dollars = 0;
    let tracked = 0;
    for (const trade of trades) {
        const total = totalDriftAt(trade, interval);
        if (total === null || total === undefined || !Number.isFinite(total))
            continue;
        dollars += total;
        tracked++;
    }
    return { dollars, tracked, trades: trades.length };
}
/**
 * Pick the interval to show: the operator's last choice when the server still
 * offers it, else the default, else the closest thing on offer. Guards against
 * a stored interval that a later deploy stopped sampling.
 */
function resolveDriftInterval(available, preferred) {
    if (available.length === 0)
        return exports.DEFAULT_DRIFT_INTERVAL_S;
    if (preferred !== undefined && available.includes(preferred))
        return preferred;
    if (available.includes(exports.DEFAULT_DRIFT_INTERVAL_S))
        return exports.DEFAULT_DRIFT_INTERVAL_S;
    // Closest by seconds. "user" has no position on that axis, so it is only
    // ever reached when nothing numeric is on offer.
    const numeric = available.filter((i) => typeof i === "number");
    if (numeric.length === 0)
        return available[0];
    return numeric.reduce((best, s) => Math.abs(s - exports.DEFAULT_DRIFT_INTERVAL_S) <
        Math.abs(best - exports.DEFAULT_DRIFT_INTERVAL_S)
        ? s
        : best);
}
const usd = (value) => `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})}`;
exports.usd = usd;
/** Signed dollars with a class hook for green/red styling. */
function pnlCell(value) {
    const cls = value > 0 ? "pnl-pos" : value < 0 ? "pnl-neg" : "pnl-zero";
    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `<span class="${cls}">${sign}${(0, exports.usd)(Math.abs(value))}</span>`;
}
function esc(text) {
    const el = document.createElement("div");
    el.textContent = text;
    return el.innerHTML;
}
function fmtTime(iso) {
    if (!iso)
        return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
function fmtDate(iso) {
    if (!iso)
        return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}
/**
 * Render the (accumulated) list of events the operator traded. When `hasMore`
 * is true a "Load more history" button is appended — PM-US pages the history
 * a window at a time (see the handler), so older events load on demand.
 */
function renderPnlEventsList(events, container, opts = {}) {
    const loadMore = opts.hasMore
        ? `<button id="pnl-load-more" class="btn btn-small pnl-load-more">Load more history</button>`
        : "";
    if (events.length === 0) {
        const message = opts.hasMore
            ? "No traded events in this range yet — load more history to look further back."
            : "No traded events found for this venue and league.";
        container.innerHTML = `<div class="pnl-empty muted">${message}</div>${loadMore}`;
        return;
    }
    // The list is navigation-only (no per-event PnL — that would be partial
    // across page boundaries). Order by most recent activity.
    const sorted = [...events].sort((a, b) => (b.lastActivityTs ?? "").localeCompare(a.lastActivityTs ?? ""));
    const rows = sorted
        .map((e) => {
        const when = e.lastActivityTs ? fmtDate(e.lastActivityTs) : "";
        return `
			<div class="pnl-event-item" data-event-id="${esc(e.eventId)}">
				<div class="pnl-event-title">${esc(e.title)}</div>
				<div class="pnl-event-meta muted">${when ? esc(when) : "Tap for P&amp;L"}</div>
			</div>`;
    })
        .join("");
    container.innerHTML = `${rows}${loadMore}`;
}
/**
 * The trade table, tinted by each row's drift at `interval`. Untracked rows
 * (sells, and anything the sampler missed) show an em dash and no tint — an
 * unknown EV must never read as a flat one.
 */
function renderTradeRows(trades, opts) {
    if (trades.length === 0) {
        return `<p class="muted">No individual trades available.</p>`;
    }
    const { interval, showEv } = opts;
    // Per-trade realized only exists on some venue-sourced rows: a logged leg has
    // no such notion (a position's realized can't be split across its fills —
    // ai/PNL_PAGE.md §8). Drop the column rather than show one of pure em dashes.
    const showRealized = trades.some((t) => t.realizedPnlDollars !== undefined);
    // Fees sit NEXT TO cost rather than inside it: the summary's Cost tile is
    // fee-exclusive and Net subtracts fees separately, so a fee-inclusive Cost
    // column here would disagree with the tiles right above it.
    const showFees = trades.some((t) => t.feesDollars !== undefined);
    const rows = trades
        .map((t) => {
        const drift = showEv ? driftAt(t, interval) : undefined;
        const tier = driftTier(drift);
        // Per share drives the tint (price quality, comparable across sizes);
        // the total is what the trade was actually worth.
        const total = showEv ? totalDriftAt(t, interval) : undefined;
        const evCell = showEv
            ? `<td class="pnl-num pnl-ev-cell">${fmtDrift(drift)}</td>
				<td class="pnl-num">${total === null || total === undefined ? "—" : pnlCell(total)}</td>`
            : "";
        return `
			<tr${tier ? ` class="${tier}"` : ""}>
				<td>${esc(fmtTime(t.ts))}</td>
				<td>${esc(t.marketTitle)}</td>
				<td>${esc(t.fill ? `${t.sideLabel} · ${t.fill}` : t.sideLabel)}</td>
				<td class="pnl-num">${t.fill ? "—" : `${t.priceCents}¢`}</td>
				<td class="pnl-num">${t.quantity}</td>
				<td class="pnl-num">${(0, exports.usd)(t.costDollars)}</td>
				${showFees
            ? `<td class="pnl-num">${t.feesDollars === undefined ? "—" : (0, exports.usd)(t.feesDollars)}</td>`
            : ""}
				${evCell}
				${showRealized
            ? `<td class="pnl-num">${t.realizedPnlDollars === undefined
                ? "—"
                : pnlCell(t.realizedPnlDollars)}</td>`
            : ""}
			</tr>`;
    })
        .join("");
    // Only filled BUYS are price-tracked, so an all-sell view is legitimately
    // blank; say so once rather than leaving a column of dashes unexplained.
    const note = showEv && !trades.some((t) => driftAt(t, interval) !== undefined)
        ? `<p class="muted pnl-ev-note">No EV data for these trades — only filled buys are price-tracked.</p>`
        : "";
    return `
		<div class="pnl-table-scroll">
			<table class="pnl-trades-table">
				<thead>
					<tr>
						<th>Time</th><th>Market</th><th>Side</th>
						<th>Price</th><th>Qty</th><th>Cost</th>
						${showFees ? `<th>Fees</th>` : ""}
						${showEv
        ? `<th>EV/sh @${(0, exports.intervalLabel)(interval)}</th><th>EV @${(0, exports.intervalLabel)(interval)}</th>`
        : ""}${showRealized ? `<th>Realized</th>` : ""}
					</tr>
				</thead>
				<tbody>${rows}</tbody>
			</table>
		</div>${note}`;
}
/**
 * Inner content of the EV summary tile. Split out because the tile is re-rendered
 * in place when the interval changes (see applyDriftInterval).
 */
function renderEvTileBody(trades, interval) {
    const { dollars, tracked, trades: total } = summarizeEv(trades, interval);
    if (tracked === 0) {
        // The table's own note explains why; don't assert a $0.00 edge here.
        return `<div class="pnl-tile-value muted">—</div>`;
    }
    // Only flag partial coverage — a full sum needs no caveat.
    const note = tracked < total
        ? `<div class="muted pnl-tile-note">${tracked} of ${total} trades tracked</div>`
        : "";
    return `<div class="pnl-tile-value">${pnlCell(dollars)}</div>${note}`;
}
/**
 * The interval picker. Rendered from the server's own list, so widening the
 * sampler's offsets needs no client change. Empty when the server offers none
 * (an older build), which leaves the table on its default interval.
 */
function renderIntervalChips(intervals, selected) {
    if (intervals.length === 0)
        return "";
    const chips = intervals
        .map((i) => `<button type="button" class="pnl-ev-chip${i === selected ? " is-selected" : ""}${i === "user" ? " is-user" : ""}" data-interval="${i}"${i === selected ? ` aria-pressed="true"` : ""}>${(0, exports.intervalLabel)(i)}</button>`)
        .join("");
    return `
		<div class="pnl-ev-intervals" role="group" aria-label="EV measurement interval">
			<span class="muted pnl-ev-intervals-label">EV after</span>${chips}
		</div>`;
}
/**
 * Re-point an already-rendered detail view at a different EV interval: swaps the
 * trade table and the chip selection in place, so the operator keeps their
 * scroll position while comparing intervals.
 */
function applyDriftInterval(container, trades, interval) {
    const table = container.querySelector("#pnl-trades");
    // Only reachable from the chips, which exist only when EV is on show.
    if (table)
        table.innerHTML = renderTradeRows(trades, {
            interval,
            showEv: true,
        });
    // The summary tile measures the same interval — it must move with it.
    const label = container.querySelector("#pnl-ev-label");
    if (label)
        label.textContent = `EV @${(0, exports.intervalLabel)(interval)}`;
    const evTotal = container.querySelector("#pnl-ev-total");
    if (evTotal)
        evTotal.innerHTML = renderEvTileBody(trades, interval);
    for (const chip of Array.from(container.querySelectorAll(".pnl-ev-chip"))) {
        const selected = (0, exports.parseInterval)(chip.dataset.interval) === interval;
        chip.classList.toggle("is-selected", selected);
        if (selected)
            chip.setAttribute("aria-pressed", "true");
        else
            chip.removeAttribute("aria-pressed");
    }
}
/**
 * Render the detail view (summary tiles + per-market rows + trade table).
 * `driftInterval` selects which post-trade interval the EV column and row tints
 * are measured at; it is resolved against what the server actually offers.
 */
function renderPnlDetail(detail, container, 
/** `title`: the list row's name — a Novig event id is a UUID. */
opts = {}) {
    const { summary, markets, trades } = detail;
    const intervals = detail.driftIntervals ?? [];
    const interval = resolveDriftInterval(intervals, opts.driftInterval);
    // Trades normally come from our own order log. Falling back to the venue's
    // fills means we logged nothing for this event, and no row can be scored —
    // worth saying, since it's the one case with no EV column at all.
    const fromVenue = detail.tradesSource === "venue";
    const tradesNote = fromVenue
        ? ` <span class="muted pnl-trades-source">from the exchange</span>`
        : "";
    // No EV column at all for venue-sourced trades (nothing to score) or against
    // a server that doesn't sample — better than a column of em dashes.
    const showEv = !fromVenue && intervals.length > 0;
    const marketRows = markets
        .map((m) => {
        // A DB-computed position not yet realized shows the money in it, not
        // a loss (owner/venue rows carry no `openCostDollars` and no badge).
        const open = m.openCostDollars !== undefined;
        const openBadge = open ? ` <span class="pnl-open-badge">open</span>` : "";
        const value = open
            ? `<span class="muted">${(0, exports.usd)(m.openCostDollars ?? 0)} in</span>`
            : pnlCell(m.realizedPnlDollars);
        return `
			<div class="pnl-market-row">
				<span>${esc(m.title)}${openBadge}</span>
				<span>${value}</span>
			</div>`;
    })
        .join("");
    const openCost = summary.openCostDollars ?? 0;
    const feesTile = summary.feesAvailable
        ? `<div class="pnl-tile-value">${(0, exports.usd)(summary.feesDollars)}</div>`
        : `<div class="pnl-tile-value muted">—</div>`;
    const feesNote = summary.feesAvailable
        ? ""
        : `<p class="muted pnl-fees-note">Fees aren't reported by PM-US activity data — "Net" equals realized here.</p>`;
    container.innerHTML = `
		<button id="pnl-detail-back" class="btn btn-small">← Back to list</button>
		<h2 class="pnl-detail-title">${esc(opts.title || detail.eventId)}</h2>
		<div class="pnl-summary${showEv ? " has-ev" : ""}">
			<div class="pnl-tile">
				<div class="muted">Net (after fees)</div>
				<div class="pnl-tile-value">${pnlCell(summary.netPnlDollars)}</div>
			</div>
			<div class="pnl-tile">
				<div class="muted">Realized</div>
				<div class="pnl-tile-value">${pnlCell(summary.realizedPnlDollars)}</div>
			</div>
			<div class="pnl-tile">
				<div class="muted">Fees</div>
				${feesTile}
			</div>
			${openCost > 0
        ? `<div class="pnl-tile pnl-open-tile">
				<div class="muted">In open markets</div>
				<div class="pnl-tile-value muted">${(0, exports.usd)(openCost)}</div>
			</div>`
        : ""}
			${showEv
        ? `<div class="pnl-tile">
				<div class="muted" id="pnl-ev-label">EV @${(0, exports.intervalLabel)(interval)}</div>
				<div id="pnl-ev-total">${renderEvTileBody(trades, interval)}</div>
			</div>`
        : ""}
		</div>
		${feesNote}
		${openCost > 0
        ? `<p class="muted pnl-open-note">Open markets aren't in Realized or Net until they settle.</p>`
        : ""}
		${markets.length
        ? `<h3 class="pnl-markets-heading">Realized by market</h3>`
        : ""}
		<div class="pnl-markets">${marketRows}</div>
		<h3 class="pnl-trades-heading">Trades${tradesNote}</h3>
		${showEv ? renderIntervalChips(intervals, interval) : ""}
		<div id="pnl-trades">${renderTradeRows(trades, { interval, showEv })}</div>`;
}
